import React from 'react';
import styled from 'styled-components';
import { useOrders } from '../hooks/useOrders';
import { format } from 'date-fns';
import { formatOrderReference } from '../utils/orderReference';

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
  font-size: 20px;
`;

const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-weight: 600;
  padding: 10px 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid #f1f5f9;
`;

const OrderCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  background: #f9fafb;
`;

const OrderCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const OrderId = styled.div`
  font-weight: 700;
  color: #1f2937;
`;

const OrderTotal = styled.div`
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
`;

const OrderMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #e5e7eb;
  color: #374151;
  text-transform: capitalize;
`;

export default function MyOrders() {
  const { orders, loading, error } = useOrders();

  return (
    <Container>
      <Card>
        <Title>My Orders</Title>
        {loading && <p>Loading orders…</p>}
        {error && <p style={{color:'#ef4444'}}>Error: {String(error)}</p>}
        {!loading && orders.length === 0 && <p>No orders yet.</p>}
        {!loading && orders.length > 0 && (
          <>
            <DesktopOnly>
              <Table>
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th>Order ID</Th>
                    <Th>Status</Th>
                    <Th>Payment</Th>
                    <Th>Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <Td>{o.created_at ? format(new Date(o.created_at), 'yyyy-MM-dd HH:mm') : '-'}</Td>
                      <Td>#{formatOrderReference(o)}</Td>
                      <Td>{o.status}</Td>
                      <Td>{o.payment_status}</Td>
                      <Td>{Number(o.grand_total).toFixed(2)} {o.currency}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </DesktopOnly>

            <MobileOnly>
              {orders.map((o) => (
                <OrderCard key={o.id}>
                  <OrderCardTop>
                    <OrderId>#{formatOrderReference(o)}</OrderId>
                    <OrderTotal>{Number(o.grand_total).toFixed(2)} {o.currency}</OrderTotal>
                  </OrderCardTop>
                  <OrderMeta>
                    {o.created_at ? format(new Date(o.created_at), 'yyyy-MM-dd HH:mm') : '-'}
                  </OrderMeta>
                  <BadgeRow>
                    <StatusBadge>{o.status}</StatusBadge>
                    <StatusBadge>{o.payment_status}</StatusBadge>
                  </BadgeRow>
                </OrderCard>
              ))}
            </MobileOnly>
          </>
        )}
      </Card>
    </Container>
  );
}
